/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {FunctionComponent} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {AssetType, BaseAsset, HistoryEventByAsset} from '@ion/core/src/types/entities';
import {AssetTypeIcon} from '@ion/dls/src/icons/AssetTypeIcon';
import {BatteryLowIcon} from '@ion/dls/src/icons/BatteryLowIcon';
import {HarshEventIcon} from '@ion/dls/src/icons/HarshEventIcon';
import {IgnitionOffIcon} from '@ion/dls/src/icons/IgnitionOffIcon';
import {IgnitionOnIcon} from '@ion/dls/src/icons/IgnitionOnIcon';
import {InputEventIcon} from '@ion/dls/src/icons/InputEventIcon';
import {StopIcon} from '@ion/dls/src/icons/StopIcon';
import Address from '@ion/dls/src/maps/components/Address';
import {getSevereEventType} from '@ion/dls/src/maps/helpers/MapViewUtils';
import MiniVideoPlayer from '../../components/videoplayer/MiniVideoPlayer';
import {dlsColor} from '../../theme/theme';
import {IconProps, IconSize} from '../../types/types';
import {getFormattedDate} from '../../utils/ComponentUtils';
import {LayerSettingsStyles} from '../layers/LayerSettings';

export const popupContentStyle = 'justify-content: left;margin-top: 15px;margin-bottom: 15px;';

// defines the styles used by the Event Detail Popup
const useStyles = makeStyles(theme => (LayerSettingsStyles()));

/**
 * @private
 */
const size = 12;

/**
 * Label styles
 */
const fontSize = '10px';
const width    = '40%';

/**
 * Event Icons Mapper
 */
const eventIconMap = {
  [HistoryEventByAsset.IGON]        : IgnitionOnIcon,
  [HistoryEventByAsset.IGOFF]       : IgnitionOffIcon,
  [HistoryEventByAsset.STOP]        : StopIcon,
  [HistoryEventByAsset.ABATLO]      : BatteryLowIcon,
  [HistoryEventByAsset.INPUTCHANGE] : InputEventIcon
};

interface EventDetailPopupProps {
  asset: BaseAsset,
  eventName: string,
  hasIOnVision: boolean,
  setIONVision: Function
};

type eventIconMapProps = {
  [HistoryEventByAsset.IGON]: React.JSX.Element,
  [HistoryEventByAsset.IGOFF]: React.JSX.Element,
  [HistoryEventByAsset.STOP]: React.JSX.Element,
  [HistoryEventByAsset.ABATLO]: React.JSX.Element,
  [HistoryEventByAsset.INPUTCHANGE]: React.JSX.Element
}

// Defines a utility for merging severe event icons with eventIconMap.
const addSevereEvent = (eventIconMap: eventIconMapProps) => {
  const map = {...eventIconMap};
  getSevereEventType()?.forEach(event => {
    map[event] = HarshEventIcon;
  });
  return map;
};

/**
 * @EventDetailPopup
 *
 * The Event Details Popup provides a component which allows users to show some
 * AVL information when hovering on Icon on the History Layer.
 *
 *
 * ```typescript
 *
 *  <EventDetailPopup asset={selectedAsset} />
 *
 * ```
 */
export const EventDetailPopup = ({asset, eventName, hasIOnVision, setIONVision}: EventDetailPopupProps) => {
  const classes: any = useStyles();
  const eventIcons   = addSevereEvent(eventIconMap);
  const icon         = eventIcons[asset?.eventType];
  const EventIcon: FunctionComponent<IconProps> = icon || null;
  const {VideoURL, eventType} = asset || {};
  const eventLabel = eventType
    ? getEventName({from: EventCodeValueType.eventType, val: eventType})
    : eventName;
  const time         = getFormattedDate(asset?.eventTime);
  return (
    <div style={{margin: '10px'}}>
      {icon && <EventIcon size={size} />}
      <label style={{paddingLeft: icon ? '10px' : '0px', fontWeight: 600}}>
        {eventLabel?.toUpperCase()}
      </label>
      {!isNil(VideoURL) && hasIOnVision && (
        <div style={{paddingTop: '5px'}}>
          <MiniVideoPlayer urls={[VideoURL]}
            handleOpenModal={function() {
              setIONVision(asset);
            }}
            isEventPopup
            isMiniPlayer
            audio
            isControls />
        </div>
      )}
      <div style={{paddingTop: '10px'}}>
        <table style={{width: '100%'}}>
          <tbody>
            <tr>
              <td style={{width}}>
                <label className={classes.legendLabel} style={{fontSize}}>
                  {translate('maps:history.legendByAsset.popUp.asset')}
                </label>
              </td>
              <td>
                <AssetTypeIcon assetType={AssetType[asset?.assetType || 'Vehicle']}
                  size={IconSize.medium}
                  color={asset?.assetType === AssetType.Tool ? dlsColor('grey1') : dlsColor('grey4')} />
                <label className={classes.legendLabel} style={{fontSize, position: 'absolute', paddingTop: '5px'}}>
                  {asset?.assetName}
                </label>
              </td>
            </tr>
            <tr>
              <td style={{width}}>
                <label className={classes.legendLabel} style={{fontSize}}>
                  {translate('maps:history.legendByAsset.popUp.operator')}
                </label>
              </td>
              <td>
                <label className={classes.legendLabel} style={{fontSize}}>{asset?.operatorNames}</label>
              </td>
            </tr>
            <tr>
              <td style={{width}}>
                <label className={classes.legendLabel} style={{fontSize}}>
                  {translate('maps:history.legendByAsset.popUp.location')}
                </label>
              </td>
              <td>
                <Address textAlign={'left'}
                  paddingLeft={'0px'}
                  street={asset?.street}
                  city={asset?.city}
                  state={asset?.state}
                  zipcode={asset?.postalCode} />
              </td>
            </tr>
            <tr>
              <td style={{width}}>
                <label className={classes.legendLabel} style={{fontSize}}>
                  {translate('maps:history.legendByAsset.popUp.time')}
                </label>
              </td>
              <td>
                <label className={classes.legendLabel} style={{fontSize}}>{time}</label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventDetailPopup;
